import type {
  MRT_ColumnFiltersState,
  MRT_SortingState,
} from 'mantine-react-table';
import React from 'react';
import {ModuleEnum, PermissionOperationEnum} from 'src/api/ProactApiClient';
import {TableRowActions} from 'src/design/molecules/TableRowActions';
import {useTable, useTableState} from 'src/design/organisms/useTable';
import {TableToolbar} from 'src/design/molecules/TableToolbar';
import {useGetCourseVideoListV2Query} from 'src/redux/queries/courseVideo';
import {useHasModulePermission} from 'src/shared/auth/useHasModulePermission';
import {PathUtility} from 'src/shared/constants/PathBuilder';
import {useCourseVideoColumns} from './hooks/useCourseVideoColumns';
import {useAppNavigate} from 'src/shared/hooks/useAppNavigate';

export interface CourseVideoTableProps {
  identifier: string;
  defaultSorting?: MRT_SortingState;
  defaultColumnFilters?: MRT_ColumnFiltersState;
}

export const CourseVideoTable: React.FC<CourseVideoTableProps> = ({
  identifier,
  defaultSorting,
  defaultColumnFilters,
}) => {
  const navigate = useAppNavigate();
  const columns = useCourseVideoColumns();

  const {hasPermission: hasCreatePermission} = useHasModulePermission({
    module: ModuleEnum.Training,
    operation: PermissionOperationEnum.Create,
  });

  const tableState = useTableState({
    identifier: identifier,
    columns: columns,
    defaultDensity: 'xs',
    defaultPagination: {
      pageIndex: 0,
      pageSize: 20,
    },
    defaultSorting: defaultSorting ?? [{id: 'title', desc: false}],
    defaultShowColumnFilters: true,
    defaultColumnFilters: defaultColumnFilters ?? [],
  });

  const {start, length, filters, filterFns, sorting} = tableState;

  const {data, isLoading, isError, isFetching, refetch} =
    useGetCourseVideoListV2Query(
      {
        start: start,
        length: length,
        filters: filters,
        filterFns: filterFns,
        sorting: sorting,
      },
      {refetchOnMountOrArgChange: true}
    );

  const rows = data?.data ?? [];
  const totalCount = data?.totalRecords ?? 0;

  const {table} = useTable({
    ...tableState,
    tableOptions: {
      manualSorting: true,
      manualFiltering: true,
      manualPagination: true,
      columns: columns,
      data: rows,
      renderRowActions: ({row}) => (
        <TableRowActions>
          <TableRowActions.View
            onClick={() =>
              navigate(PathUtility.CourseVideoView(row.original.id, 'content'))
            }
          />
        </TableRowActions>
      ),
      renderTopToolbarCustomActions: () => (
        <TableToolbar>
          <TableToolbar.Actions>
            {hasCreatePermission && (
              <TableToolbar.Action
                variant="create"
                onClick={() => navigate(PathUtility.CourseVideoCreate())}
              />
            )}
          </TableToolbar.Actions>
        </TableToolbar>
      ),
    },
    totalCount,
    isError,
    isLoading,
    isFetching,
    onRefresh: refetch,
  });

  return <>{table}</>;
};
